import React, { ReactElement, useEffect, useMemo } from "react";
import Header from "./components/Header";
import VerticalScroller from "./components/VerticalScroller";
import { SiteContentProvider, useSiteContent } from "./SiteContentContext";
import { ColorModeProvider, SITE_DEFAULT_THEME, useColorMode } from "./ColorModeContext";
import { DemoDrawerProvider } from "./DemoDrawerContext";
import { LegalDrawerProvider } from "./LegalDrawerContext";
import DemoDrawer from "./components/DemoDrawer";
import LegalDrawer from "./components/LegalDrawer";
import LegalDeepLink from "./components/LegalDeepLink";
import DemoDeepLink from "./components/DemoDeepLink";
import { LeadChatProvider } from "./LeadChatContext";
import LeadChatLauncher from "./components/leadChat/LeadChatLauncher";
import { CookieConsentProvider } from "@Components/consent/CookieConsentProvider";
import { CssBaseline } from '@mui/material';
import { createTheme, responsiveFontSizes, Theme, ThemeProvider } from '@mui/material/styles';
import getTheme from "@Components/theme/base";
import { setThemeColorMeta } from "@Components/theme/themeColorMeta";
import GlobalStyles from "@mui/material/GlobalStyles";
import { MotionConfig } from "framer-motion";
import { useTranslation } from "react-i18next";

export interface ISections {
  name: string;
  component: ReactElement;
}

const SiteNotifications: React.FC = () => {
  const { content } = useSiteContent();
  const notifications = content.app?.notifications;

  useEffect(() => {
    const title = notifications?.title;
    const body = notifications?.body;
    const icon = notifications?.icon;

    if (!title || !body) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    if ("Notification" in window && "serviceWorker" in navigator) {
      if (Notification.permission === "default") {
        Notification.requestPermission().then((permission) => {
          if (permission === "granted") {
            navigator.serviceWorker.ready.then((registration) => {
              registration.showNotification(title, {
                body,
                icon: icon ?? "./assets/logo/logo.svg"
              });
            });
          }
        });
      }
    }
  }, [notifications?.body, notifications?.icon, notifications?.title]);

  return null;
};

// The site renders with the same theme catalogue as the app (the visitor
// picks one in the header; Fidava Dark by default). The app themes are built on a
// 4px spacing unit, while every site layout is written against MUI's 8px unit
// (theme.spacing(14) = 112px hero padding, …) — so the selected app theme is
// rebuilt here with its palette, typography, shape and component overrides
// intact and only the spacing unit changed.
// Headings scale down with the viewport (responsiveFontSizes): the app's
// h1–h4 are fixed rem sizes, and in the Futuristic themes they're wide
// uppercase Orbitron — a 48px "ASSISTENTE" alone was wider than a phone.
const siteThemeFrom = (appTheme: Theme): Theme =>
  responsiveFontSizes(
    createTheme({
      palette: appTheme.palette,
      typography: appTheme.typography as any,
      shape: appTheme.shape,
      shadows: appTheme.shadows,
      components: appTheme.components
    }),
    { factor: 3 }
  );

const ThemedShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { themeName } = useColorMode();
  const marketingTheme = useMemo(
    () => siteThemeFrom(getTheme(themeName) ?? getTheme(SITE_DEFAULT_THEME)),
    [themeName]
  );

  // Mobile browser bar follows the page colour of the selected theme.
  useEffect(() => {
    setThemeColorMeta(marketingTheme.palette.background.default);
  }, [marketingTheme]);

  return (
    <ThemeProvider theme={marketingTheme}>
      {/* CssBaseline paints body with the theme's background (for the
          Futuristic family: gradient glow + neon grid), so the shell stays
          transparent and lets it show through. */}
      <CssBaseline />
      {/* Form controls don't inherit font-family by default, so raw <button>s
          (nav links, tabs, toggles) rendered in the browser's Arial instead of
          the theme font. MUI components set their own font and are unaffected. */}
      <GlobalStyles
        styles={{
          "button, input, select, textarea": { font: "inherit" },
          // Safety net for long words in wide display fonts on phones: wrap
          // (hyphenated where the language allows) instead of overflowing.
          "h1, h2, h3, h4, h5, h6": { overflowWrap: "break-word", hyphens: "auto" },
          // Same for body copy: German compounds ("Sozialversicherungssätze")
          // were wider than narrow cards on a 320px phone.
          "p, li": { overflowWrap: "break-word", hyphens: "auto" }
        }}
      />
      <div
        style={{
          minHeight: "100vh",
          color: marketingTheme.palette.text.primary,
          transition: "color 200ms ease"
        }}
      >
        {children}
      </div>
    </ThemeProvider>
  );
};

export default function App() {
  const { i18n } = useTranslation();
  // Marketing site reads the language straight from i18n (no Redux) so the
  // /fidava bundle doesn't pull the app store. i18n is initialised from the
  // persisted language at boot, so this still honours the user's choice.
  const preferredLocale = i18n.language;

  return (
    <SiteContentProvider initialLocale={preferredLocale}>
      <ColorModeProvider>
        <DemoDrawerProvider>
         <LegalDrawerProvider>
          <LeadChatProvider>
          {/* `reducedMotion="user"` makes every framer-motion animation
              honour the visitor's `prefers-reduced-motion: reduce`
              system setting — opacity transitions still play (essential
              for layout), but translate/scale/rotate are flattened.
              Pairs with the CSS-keyframe override in VerticalScroller
              so JS-driven and CSS-driven animations both stand down. */}
          <MotionConfig reducedMotion="user">
            <ThemedShell>
             <CookieConsentProvider>
              <SiteNotifications />
              <Header />
              {/* <main> landmark so screen-reader users can jump straight to
                  the page content (Lighthouse a11y flag — the page had none). */}
              <main
                className="App"
                style={{
                  height: "100%",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <VerticalScroller />
              </main>
              <DemoDrawer />
              <LegalDrawer />
              {/* Opens the legal drawer from /fidava?legal=privacy|terms|cookie
                  without leaving the route. */}
              <LegalDeepLink />
              {/* Opens the demo-scheduling drawer from /fidava?demo=1
                  without leaving the route. */}
              <DemoDeepLink />
              {/* Website chat: FAQ bot with handoff to the Sales team.
                  The widget itself lazy-loads on first open. */}
              <LeadChatLauncher />
             </CookieConsentProvider>
            </ThemedShell>
          </MotionConfig>
          </LeadChatProvider>
         </LegalDrawerProvider>
        </DemoDrawerProvider>
      </ColorModeProvider>
    </SiteContentProvider>
  );
}
